import { gsap } from 'gsap'

/**
 * GSAP "Lire plus" toggle (height + opacity + icon rotation).
 * Supporte désormais les SVG + / − à la place du texte.
 *
 * Markup contract:
 *  - button[data-content-toggle="ID"]
 *  - #ID-extended (panel, has class 'hidden' when collapsed)
 *  - #ID-toggle-text (holds label, has data-more-text / data-less-text OR data-more-svg / data-less-svg)
 *  - #ID-icon (arrow svg rotated 0/180)
 */
export function initContentToggle(context = document) {
  context.querySelectorAll('[data-content-toggle]').forEach(btn => {
    if (btn.__initToggle) return; // guard
    const id    = btn.getAttribute('data-content-toggle');
    const panel = context.querySelector(`#${id}-extended`);
    const txt   = context.querySelector(`#${id}-toggle-text`);
    const icon  = context.querySelector(`#${id}-icon`);
    if (!panel || !txt || !icon) return;
    btn.__initToggle = true;

    const collapsed = panel.classList.contains('hidden');
    gsap.set(panel, collapsed ? {height: 0, opacity: 0, overflow: 'hidden'} : {height: 'auto', opacity: 1});
    let open = !collapsed;

    // ✅ Initialise le contenu du toggle selon l’état
    updateToggleContent(txt, open ? 'less' : 'more');

    btn.addEventListener('click', () => {
      if (open) {
        // Collapse
        const h = panel.getBoundingClientRect().height;
        gsap.timeline()
          .set(panel, {overflow: 'hidden'})
          .to(panel, {height: h, duration: 0})
          .to(panel, {height: 0, opacity: 0, duration: 0.45, ease: 'power2.inOut'})
          .add(() => { panel.classList.add('hidden'); panel.style.height = '0px'; });
        rotate(icon, 0);
        swap(txt, { text: txt.dataset.moreText, svg: txt.dataset.moreSvg });
        open = false;
      } else {
        // Expand
        panel.classList.remove('hidden');
        const target = panel.scrollHeight;
        gsap.timeline()
          .set(panel, {height: 0, opacity: 0, overflow: 'hidden'})
          .to(panel, {height: target, duration: 0.45, ease: 'power2.out'})
          .to(panel, {opacity: 1, duration: 0.3, ease: 'power2.out'}, 0.05)
          .add(() => { panel.style.height = 'auto'; panel.style.overflow = 'visible'; });
        rotate(icon, 180);
        swap(txt, { text: txt.dataset.lessText, svg: txt.dataset.lessSvg });
        open = true;
      }
    });
  });
}

function swap(el, { text, svg }) {
  gsap.to(el, {
    opacity: 0,
    duration: 0.15,
    onComplete: () => {
      updateToggleContent(el, svg ? (svg.includes('<svg') ? 'svg' : null) : 'text', text, svg);
      gsap.to(el, { opacity: 1, duration: 0.2 });
    }
  });
}

/**
 * Met à jour le contenu (texte ou SVG) du toggle.
 * @param {HTMLElement} el 
 * @param {string} type - "more" ou "less"
 */
function updateToggleContent(el, type, text = null, svg = null) {
  // Choisir la bonne source
  const useSvg = svg || el.dataset[`${type}Svg`];
  const useText = text || el.dataset[`${type}Text`];

  if (useSvg) {
    el.innerHTML = useSvg;
  } else if (useText) {
    el.textContent = useText;
  }
}

function rotate(icon, deg) {
  gsap.to(icon, { rotation: deg, duration: 0.3, ease: 'power2.out' });
}

// Legacy helper
window.toggleExtendedContent = id => {
  const b = document.querySelector(`[data-content-toggle="${id}"]`);
  if (b) b.click();
};
